<template>
  <v-menu
    class="date-time-range"
    v-model="isMenuOpen" 
    offset-y
    :close-on-content-click="false"
  >
    <template #activator="{}">
      <UiInputWrapper
        class="date-time-range__text-field-wrapper"
        label="Time range"
      >
        <input
          type="text"
          :value="selectText"
          class="date-time-range__text-field date-time-range__text-field--activator"
          readonly
          @click="isMenuOpen = true"
        />

        <template #input-slot-right>
          <v-icon
            @click="isMenuOpen = true"
            :class="{
              'date-time-range__field-arrow-icon': true,
              'date-time-range__field-arrow-icon--rotated': isMenuOpen,
            }"
          >
            mdi-menu-down
          </v-icon>
        </template>
      </UiInputWrapper>
    </template>

    <v-card class="date-time-range__menu">
      <div class="date-time-range__menu-box">
        <div class="date-time-range__menu-left">
          <v-date-picker
            v-model="pickerDates"
            range
            :min="min ? dateToIsoDateString(min) : undefined"
            :max="max ? dateToIsoDateString(max) : undefined"
            @change="handlePickerInput"
          />

          <div class="date-time-range__menu-text-fields">
            <UiInputWrapper
              label="From"
              :is-valid="isValidFrom"
              :hints="DATE_TIME_RANGE_HINTS"
              preserve-hints-on-error
            >
              <input
                :value="fromInput"
                type="text"
                class="date-time-range__text-field"
                @input="(e) => onFromInput(e.target.value)"
              />
            </UiInputWrapper>

            <UiInputWrapper
              label="To"
              :is-valid="isValidTo"
              :hints="DATE_TIME_RANGE_HINTS"
              preserve-hints-on-error
            >
              <input
                v-model="toInput"
                type="text"
                class="date-time-range__text-field"
                @input="(e) => onToInput(e.target.value)"
              />
            </UiInputWrapper>
          </div>
        </div>

        <v-list outlined dense>
          <v-subheader class="date-time-range__predefined-title">
            Preset Time Ranges
          </v-subheader>

          <v-list-item-group
            v-model="predefinedRangeIdx"
            @change="setRange"
          >
            <v-list-item
              class="date-time-range__predefined-range-item"
              v-for="(predefinedRangesItem, i) of PREDEFINED_RANGES"
              :key="i" 
            >
              {{ predefinedRangesItem.text }}
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>
      <v-divider/>

      <div class="date-time-range__menu-buttons">
        <XBtn
          text="OK"
          color="primary"
          @click="() => emitRange()"
          :disabled="!isValidTo || !isValidFrom"
        />

        <XBtn
          text="Cancel"
          color="secondary"
          @click="isMenuOpen = false"
        />
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import { defineComponent, ref, computed } from "vue"
import { PREDEFINED_RANGES, useDateTimeRange } from "@/composition/date/use-date-time-range";
import { DATE_TIME_RANGE_HINTS } from "@/composition/date/use-date-range-validation";
import XBtn from "@/components/basic/XBtn.vue";
import UiInputWrapper from "@/components/basic/form/ui/UiInputWrapper.vue";
import { dateToIsoDateString } from "@/js/general";


export default defineComponent({
  name: 'DateTimeRange',

  components: {
    XBtn,
    UiInputWrapper,
  },

  props: {
    value: Object,
    min: Date,
    max: Date,
    defaultSeconds: {
      type: Number,
      default: 86400,
    },
    useStringFormat: {
      type: Boolean,
      default: false,
    }
  },

  emits: ["input"],

  setup(props, { emit }) {
    const isMenuOpen = ref(false);
    const DEFAULT_RANGE = {
      from: new Date(new Date().getTime() - props.defaultSeconds * 1000),
      to: new Date(),
      seconds: props.defaultSeconds,
    };

    // eslint-disable-next-line no-undef
    const range = ref(props.value ? structuredClone(props.value) : DEFAULT_RANGE)

    const {
      predefinedRangeIdx,
      pickerDates,
      isCustomRange,
      fromInput,
      toInput,
      isValidFrom,
      isValidTo,
      handlePickerInput,
      setRange,
      formatRangeValue,
    } = useDateTimeRange({ range, useStringFormat: props.useStringFormat, defaultSeconds: props.defaultSeconds });

    const emitRange = () => {
      if (isValidTo.value && isValidFrom.value) {
        // eslint-disable-next-line no-undef
        emit("input", structuredClone(range.value));
        isMenuOpen.value = false;
      }
    };

    const onFromInput = (value) => {
      isCustomRange.value = true;
      fromInput.value = value;
    };

    const onToInput = (value) => {
      isCustomRange.value = true;
      toInput.value = value;
    };

    const selectText = computed(() => {
      if (!isCustomRange.value) {
        return PREDEFINED_RANGES[predefinedRangeIdx.value].text;
      }
      return `${formatRangeValue(range.value.from)} - ${formatRangeValue(range.value.to)}`;
    });


    return {
      isMenuOpen,
      range,
      predefinedRangeIdx,
      pickerDates,
      isCustomRange,
      fromInput,
      toInput,
      isValidTo,
      isValidFrom,
      selectText,
      DATE_TIME_RANGE_HINTS,
      PREDEFINED_RANGES,

      dateToIsoDateString,
      handlePickerInput,
      setRange,
      emitRange,
      onFromInput,
      onToInput,
    }
  },


  computed: {
    // selectText() {
    //   let selectText = '';
    //   if (typeof this.predefinedRangeIdx !== 'undefined') {
    //     selectText = this.isCustomRange ?
    //         `${dateToDateTimeString(this.range.from)}  -  ${dateToDateTimeString(this.range.to)}` :
    //         PREDEFINED_RANGES[this.predefinedRangeIdx].text;
    //   }
    //   return selectText;
    // },
  },

  // methods: {

    // emitInput() {
    //   this.$emit('input', deepCopy(this.range));
    //   this.isMenuOpen = false;
    // },

    /**
    * @param {number} index
    */
    // setRange(index) {
    //   if (typeof index === "undefined") return
    //
    //   const range = PREDEFINED_RANGES[index];
    //   const now = new Date();
    //   this.range.from = new Date(now.getTime() - range.value * 1000);
    //   this.range.to = now;
    //   this.range.seconds = range.value;
    //   this.isCustomRange = false;
    // },
  // },
});
</script>

<style lang="scss">
.date-time-range {
  &__text-field-wrapper {
    margin-top: -7px;
    width: 321px;
  }

  &__text-field--activator {
    cursor: pointer;
  }

  &__field-arrow-icon {
    transition: .3s cubic-bezier(.25, .8, .5, 1), visibility 0s;

    &--rotated {
      transform: rotate(180deg);
    }
  }

  &__menu-box {
    display: flex;
    gap: 16px;
    padding: 16px;

    & .v-picker__title {
      display: none;
    }
  }

  &__menu-left {
    display: flex;
    flex-direction: column;
    gap: 10px;
    justify-content: space-between;
  }

  &__menu-text-fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__predefined-title {
    color: var(--v-text-base);
    font-weight: bold;
    white-space: nowrap;
  }

  &__predefined-range-item {
    font-size: 13px;
    font-weight: 500;
  }

  &__menu-buttons {
    display: flex;
    gap: 10px;
    padding: 16px;
  }

}
</style>

